/**
 * Whether the device has a real hovering pointer (mouse / trackpad).
 *
 * Evaluated once, lazily, and cached: constructing a `MediaQueryList` is not
 * free, and the previous per-component call site ran it on every card on every
 * hover. Touch devices DO fire `mouseover`/`pointerover` on tap, so on a product
 * grid that showed up as 280-328ms `pointerover` handlers — an INP failure.
 *
 * SSR-safe: returns `false` on the server, where there is no pointer at all.
 * Deliberately not reactive — a device does not gain or lose its mouse mid-
 * session in any way that matters here, and the CSS `@media (hover: hover)`
 * guard (see the `hover-supported` SCSS mixin) is what handles styling.
 */
let cached: boolean | undefined;

export const canHover = (): boolean => {
  if (cached !== undefined) return cached;
  if (typeof window === "undefined" || !window.matchMedia) return false;
  // `(hover: hover)` ONLY — deliberately not `and (pointer: fine)`. Both queries
  // report the PRIMARY pointing device, so a touchscreen Windows laptop, a touch
  // Chromebook or an iPad with a Magic Keyboard reports `pointer: coarse` even
  // with a mouse attached. Adding `pointer: fine` silently denied those users the
  // hover/secondary product image. `(hover: hover)` alone is already false on
  // phones and tablets, which is the case this guard exists for.
  cached = window.matchMedia("(hover: hover)").matches;
  return cached;
};

export default canHover;
